import React from "react";
import { Box, Flex, HStack, SimpleGrid, Text } from "@chakra-ui/react";

/**
 * KpiCard — a single KPI tile: label, big value, and either a trend pill or an
 * action cluster on the right. Supports a `code` value (monospace, navy) and an
 * optional `hint` line (template's dataKpi variant).
 * Template ref: `.kpi`, `.kpi .label`, `.kpi .value`, `.kpi .trend`, `.dataKpi`.
 *
 * Props:
 *   label      - uppercase label (string/node)
 *   value      - the big number/value (string/node)
 *   code       - when true, value renders monospace navy (e.g. a partner code)
 *   hint       - small secondary line under the label (dataKpi style)
 *   trend      - text rendered in the mint trend pill (e.g. "OK", "+3")
 *   trendColor - override trend text color
 *   actions    - right-aligned node(s) instead of a trend pill
 */
export const KpiCard = ({
  label,
  value,
  code = false,
  hint,
  trend,
  trendColor,
  actions,
  ...rest
}) => (
  <Flex
    align="center"
    justify="space-between"
    gap="12px"
    minH="58px"
    px="12px"
    py="10px"
    bg="portal.panel"
    borderWidth="1px"
    borderColor="portal.line"
    borderRadius="10px"
    boxShadow="kpi"
    {...rest}
  >
    <Box minW={0}>
      {label && (
        <Text
          textTransform="uppercase"
          fontSize="11px"
          fontWeight="800"
          letterSpacing="0.04em"
          color="portal.muted"
          sx={{ marginBottom: "0 !important" }}
        >
          {label}
        </Text>
      )}
      {value !== undefined && value !== null && (
        <Text
          mt="4px"
          fontSize="19px"
          fontWeight="900"
          letterSpacing="-0.04em"
          color={code ? "portal.primary" : "portal.text"}
          fontFamily={code ? "ui-monospace, SFMono-Regular, Menlo, monospace" : undefined}
          isTruncated
          sx={{ marginBottom: "0 !important" }}
        >
          {value}
        </Text>
      )}
      {hint && (
        <Text
          mt="3px"
          fontSize="11px"
          fontWeight="700"
          color="portal.muted"
          lineHeight="1.25"
          sx={{ marginBottom: "0 !important" }}
        >
          {hint}
        </Text>
      )}
    </Box>

    {actions ? (
      <HStack spacing="8px" flexShrink={0} flexWrap="wrap" justify="flex-end">
        {actions}
      </HStack>
    ) : (
      trend && (
        <Box
          flexShrink={0}
          fontSize="11px"
          fontWeight="800"
          px="7px"
          py="4px"
          borderRadius="999px"
          color={trendColor || "#0F766E"}
          bg="#E8F5F3"
        >
          {trend}
        </Box>
      )
    )}
  </Flex>
);

/**
 * KpiStrip — lays KpiCards out 4-across, collapsing to 2 then 1.
 * Template ref: `.kpis`.
 */
export const KpiStrip = ({ children, columns, ...rest }) => (
  <SimpleGrid columns={columns || { base: 1, md: 2, xl: 4 }} spacing="10px" {...rest}>
    {children}
  </SimpleGrid>
);

export default KpiStrip;
